import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { button, colors, font } from '../../lib/sharedStyles';
import translate from "translate";
import AddonComponent from '../SearchSpring/AddOnList';
import SvgX from "../Icons/X";


document.addEventListener("DOMContentLoaded", () => {
  const cartDrawer = document.querySelector("cart-drawer");
  if (cartDrawer) {
    cartDrawer.remove();
  }
});


interface DrawerContainerProps {
  isOpen: boolean;
  price: string;
  image: string;
  firstVariantId: string;
}

const DrawerContainer = styled(motion.div) <DrawerContainerProps>`
  position: fixed;
  top: 0;
  right: 0;
  width: 50vw;
  max-width: 600px;
  height: 100vh;
  background: white;
  padding: 32px;
  overflow-y: auto;
  z-index: 1000;

  @media (max-width: 768px) {
    left: 0;
    right: 0;
    width: 100vw;
    padding: 16px;
  }
`;


const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 18px;
`;

const Divider = styled.div`
  /* Subtract the same amount of padding your DrawerContainer has */
  margin: 12px -32px;
  width: calc(100% + 64px);

  height: 1px;
  background: #ddd;
`;


const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const Title = styled.h3`
${font("Futura", 500, "24px", "16px")}
margin: 30px 0;
`;

const ProductContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 0 0 16px 0;
`;

const ProductImageContainer = styled.div`
  width: 166px;
  height: 166px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, #e5e5e5 0%, #e5e5e5 13.7%, #eff0f0 59.6%, #f7f7f7 100%);
`;

const ProductImage = styled.img`
  max-width: 100%;
  object-fit: contain;
  border-radius: 0px;
  transform: rotate(-90deg);
  filter: drop-shadow(20px 24px 14px rgba(0,0,0,0.18));
`;

const ProductDetails = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ProductTitle = styled.h4`
  ${font('Instrument Sans', 600, '14px', '22px')};
  margin: 0;
`;

const ProductPrice = styled.span`
  ${font('Instrument Sans', 600, '14px', '22px')};
  color: #191A1F;

  text-align: right;
`;

const ItemList = styled.ul`
  ${font('Instrument Sans', 400, '12px', '20px')};
  list-style: none;
  padding: 0;
  color: #555;
  margin: 4px 0;
`;

const Item = styled.li`
  margin-bottom: 4px;
`;

const ShowMoreButton = styled.button`
  ${font('Instrument Sans', 400, '12px', '20px')};
  background: none;
  border: none;
  color: #191A1F;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  text-align: left;
  margin-top: 5px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 20px;

  @media (max-width: 600px) {

  }
`;

const ButtonOne = styled.button`
  display: flex;
  width: 48%;
  height: 60px;
  padding: 14px 32px;
  justify-content: center;
  align-items: center;
  border-width: 1px;
  gap: 16px;
  border-radius: 30px;
  flex-shrink: 0;
  cursor: pointer;
  background: white;
  ${font('Instrument Sans', 600, '16px', '22px')};
`;

const ButtonTwo = styled(ButtonOne)`
  background: #191A1F;
  display: flex;
  border-radius: 30px;
  width: 48%;
  height: 60px;
  padding: 14px 32px;
  justify-content: center;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
  color: white;
  ${font('Instrument Sans', 600, '16px', '22px')};
`;


const ModShopDrawer: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currencySymbol, setCurrencySymbol] = useState<string>('$');
  const [products, setProducts] = useState<Product[]>([]);
  const [productImage, setProductImage] = useState<string | undefined>(undefined);
  const [productPrice, setProductPrice] = useState<string | undefined>(undefined);
  const [specNames, setSpecNames] = useState<string[]>([]);
  const [showMore, setShowMore] = useState<boolean>(false);
  const [translatedTitle, setTranslatedTitle] = useState<string>('');
  const [translatedKeepShopping, setTranslatedKeepShopping] = useState<string>('');
  const [translatedContinueToCart, setTranslatedContinueToCart] = useState<string>('');
  const [translatedShowMore, setTranslatedShowMore] = useState<string>('');
  const [translatedShowLess, setTranslatedShowLess] = useState<string>('');

  const dataElement = document.getElementById('spectrum-sku');
  const locale = dataElement?.getAttribute('data-locale') || 'US';
  let searchProfile = 'us-mini-cart';

  if (locale.includes('US')) {
    searchProfile = 'us-mini-cart';
  } else {
    searchProfile = 'emea-mini-cart';
  }


  async function fetchProducts() {
    try {
      const options: RequestInit = {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      };
      const searchSpringElement = document.getElementById('react-searchspring-config');
      const searchSpringID = searchSpringElement ? searchSpringElement.getAttribute('data-site-id') : '';

      const url = `https://${searchSpringID}.a.searchspring.io/boost/zcbtly/recommend?tags=view-cart`;
      const response = await fetch(url, options);

      if (!response.ok) {
        console.log('Failed to fetch products:', response);
        throw new Error(`Failed to fetch products: ${response.status}`);
      }

      const data = await response.json();
      console.log('Raw data from Searchspring:', data);

      const results = data[0].results;
      console.log('Results:', results);

      const topThreeProducts = results.slice(0, 3);

      const addons = topThreeProducts.map((product: any) => {
        const { attributes } = product;

        const [firstVariantId] = attributes.variant_id.split('|') || [''];

        const [firstVariantPrice] = attributes.variant_price.split('|') || [''];

        let firstImageUrl = '';
        let rawMedia = attributes.variant_mfield_custom_variant_media || '';

        const [firstMediaBlock = ''] = rawMedia.split('|') || [];

        try {
          const parsed = JSON.parse(firstMediaBlock.trim());
          firstImageUrl = parsed?.variantMedia?.images?.[0]?.url || '';
        } catch (err) {
          console.error('Error parsing variant_mfield_custom_variant_media:', err);
        }

        return {
          name: product.mappings.core.name,
          firstVariantId,
          price: firstVariantPrice,
          image: firstImageUrl,
        };
      });

      setProducts(addons);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  }
  interface Product {
    id: number;
    title: string;
    image: string;
    price: string;
    firstVariantId: string;
    name: string;
  }

  useEffect(() => {
    async function fetchTranslation() {
      const dataElement = document.getElementById('spectrum-sku');
      const language = dataElement?.getAttribute('data-language') || 'en';
      const titleTranslation = await translate("Added to your Cart", language);
      setTranslatedTitle(titleTranslation);
      const shoppingTranslation = await translate("Keep Shopping", language);
      setTranslatedKeepShopping(shoppingTranslation);
      const cartTranslation = await translate("Continue to Cart", language);
      setTranslatedContinueToCart(cartTranslation);
      const showMoreTranslation = await translate("Show more", language);
      setTranslatedShowMore(showMoreTranslation);
      const showLessTranslation = await translate("Show less", language);
      setTranslatedShowLess(showLessTranslation);
      setCurrencySymbol(document.getElementById('shop-currency-symbol')?.getAttribute('data-currency-symbol') || '$');
    }
    fetchTranslation();
  }, []);
  useEffect(() => {
    if (isOpen) {
      fetchProducts();
    }
  }, [isOpen]);

  useEffect(() => {
    (window as any).toggleModShopDrawer = (payload: any) => {
      console.log(payload);
      const productImage =
        payload.thumbnailsByAngle.find((img: { angle: string; url: string }) => img.angle === 'north')?.url || 'https://via.placeholder.com/100';
      const price = payload.options.finalPrice.toString() || '3,024.99';
      setProductPrice(price);
      setProductImage(productImage);
      setSpecNames(payload.items.map((item: { name: string }) => item.name));
      setIsOpen(prev => !prev);
    };

    return () => {
      delete (window as any).toggleModShopDrawer;
    };
  }, []);


  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <>
            <Overlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
            />
            <DrawerContainer
              isOpen={isOpen}
              firstVariantId={products[0]?.firstVariantId || ''}
              price={productPrice || ''}
              image={productImage || ''}
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            >
              <Header>
                <svg xmlns="http://www.w3.org/2000/svg" width="168" height="32" viewBox="0 0 168 32" fill="none">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M56.9516 1.66486H166.119V30.3351H56.9516V1.66486ZM55.3516 0H56.9516H167.784V32H56.9516H55.3516H0V0H55.3516ZM55.3516 30.3351H1.66486V1.66486H55.3516V30.3351Z" fill="#191A1F" />
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M56.9516 1.66486H166.119V30.3351H56.9516V1.66486ZM55.3516 0H56.9516H167.784V32H56.9516H55.3516H0V0H55.3516ZM55.3516 30.3351H1.66486V1.66486H55.3516V30.3351Z" fill="url(#paint0_linear_15137_9765)" />
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M34.7275 16.9377C34.022 16.7336 33.4044 17.5707 33.2239 18.3152C33.0589 18.9944 33.146 19.9076 33.7608 20.0246C34.2532 20.1189 34.8245 19.6882 34.9715 18.8902C34.9968 18.7518 35.0438 18.4161 35.0812 18.1496C35.1014 18.0054 35.1188 17.8815 35.1283 17.8201C35.297 16.7373 35.424 16.263 35.424 16.263C36.0134 14.0031 37.4254 9.60847 37.8108 9.35183C38.2098 9.08521 39.3099 9.04078 38.9653 9.95761C38.768 10.4834 38.5588 10.9871 38.3452 11.5016L38.3452 11.5016C37.9792 12.3828 37.5999 13.2959 37.2449 14.4067C37.1596 14.6734 37.0639 14.9609 36.9645 15.2597L36.9645 15.2598C36.2948 17.2727 35.4544 19.7988 36.4469 19.9267C36.701 19.9595 36.8831 19.8649 37.0386 19.784C37.2095 19.6952 37.3485 19.6229 37.5161 19.7544C38.0049 20.1371 37.4708 20.8281 36.5285 20.9206C35.8432 20.9455 35.5281 20.745 35.2983 20.5987C35.0932 20.4682 34.9559 20.3808 34.684 20.5352C32.8413 21.5817 31.705 20.118 32.0677 18.2826C32.3778 16.7101 33.5894 15.5992 34.9424 16.2802C34.9424 16.2802 35.2889 16.4525 35.2598 16.7454C35.2372 16.9721 35.0667 17.0175 34.7266 16.9377H34.7275ZM19.1086 15.8381C18.832 15.9034 18.6207 15.985 18.5228 16.3432H18.5219C18.3405 16.9472 17.9107 18.2875 17.2514 19.9099C16.3445 22.1434 15.7542 23.4094 15.7542 23.4094C15.7542 23.4094 15.5447 23.7241 15.0912 23.7586C14.5979 23.7513 14.2234 23.7023 14.2071 23.3604C14.2797 23.1024 14.5955 22.4659 14.973 21.7051L14.973 21.7051C15.3584 20.9283 15.808 20.022 16.1287 19.2569C16.6293 18.1487 16.8932 17.3843 17.0401 16.9118C17.1217 16.7005 16.9594 16.6026 16.8125 16.7168C16.7323 16.75 16.5969 16.8112 16.4494 16.8779L16.4493 16.8779L16.4493 16.8779L16.4493 16.8779C16.2343 16.975 15.9936 17.0838 15.8603 17.1349C15.8282 17.1471 15.7943 17.1612 15.7596 17.1757L15.7595 17.1757L15.7595 17.1757C15.4788 17.2929 15.1365 17.4359 15.1502 16.7984C15.1781 16.257 15.3869 16.2049 15.6211 16.1465C15.6756 16.1328 15.7315 16.1189 15.7868 16.0983C16.1613 15.9587 17.4155 15.3982 17.4155 15.3982C17.4155 15.3982 18.2308 13.0087 18.3269 11.9468C18.4901 10.7098 18.1247 10.3534 16.9104 10.6608C15.4743 10.9969 13.865 12.1934 12.6373 13.1061L12.6373 13.1061L12.6373 13.1061C12.5564 13.1663 12.4771 13.2252 12.3997 13.2826C11.8897 13.6605 11.2256 14.2797 10.6373 14.8285C10.212 15.2251 9.82617 15.5849 9.56667 15.79C9.56667 15.79 9.14317 16.2779 8.49205 15.6268C7.89083 14.9854 8.22188 14.717 8.98076 14.1018C9.14677 13.9672 9.33325 13.8161 9.53493 13.6408C12.9066 10.9782 17.6223 8.76278 18.9145 9.83015C19.4242 10.2591 19.8005 11.0118 19.1585 13.8357C19.0394 14.3563 19.0026 14.5131 18.9932 14.5505C18.9943 14.5412 18.9953 14.5358 18.9953 14.5358C18.9953 14.5358 18.9858 14.5797 18.9932 14.5505C18.9854 14.6163 18.972 14.876 19.2718 14.7471C19.6645 14.6392 21.6233 14.0262 21.6233 14.0262C21.6233 14.0262 21.811 14.2329 21.7303 14.6655C21.6323 15.191 21.4326 15.2357 20.8319 15.3703L20.8319 15.3703L20.8317 15.3703C20.7213 15.3951 20.5974 15.4228 20.458 15.4572C20.1025 15.5216 19.523 15.702 19.1086 15.8381ZM38.969 16.862C39.0574 16.8584 39.2028 16.869 39.429 16.8854C39.7184 16.9064 40.1401 16.937 40.7438 16.9599C41.8692 17.0017 42.0923 16.1311 42.0469 15.5906L41.9727 15.6066C40.8866 15.8409 40.6051 15.9016 39.6365 15.9016C38.5972 15.9016 37.3149 16.1918 37.2914 17.1069C37.2703 17.9203 38.2209 18.1691 38.8634 18.3372C38.9386 18.3569 39.0096 18.3754 39.0742 18.3937C39.2456 18.4345 39.31 18.5723 39.1967 18.6621C39.1404 18.707 39.0689 18.755 38.9907 18.8076C38.7024 19.0013 38.3219 19.257 38.2662 19.6479C38.1656 20.3516 38.8058 20.903 39.6174 20.8866C40.6984 20.864 41.8928 20.4051 43.039 19.0285C43.2269 18.8027 43.4283 18.4868 43.6079 18.2049L43.608 18.2048L43.6081 18.2046L43.6081 18.2045C43.738 18.0008 43.8565 17.8149 43.9504 17.6936C44.0039 17.5893 44.159 17.5394 44.227 17.5467C44.432 17.592 44.7221 17.7471 44.6832 17.9702C44.6673 18.0603 44.5906 18.201 44.4979 18.3712C44.2406 18.8432 43.8601 19.5416 44.3141 20.0115C44.6931 20.4042 45.648 20.6889 46.4089 20.1358C47.1208 19.6179 46.8705 19.1446 46.5876 19.1101C46.5262 19.1026 46.4312 19.1632 46.3158 19.2367C46.0525 19.4046 45.6832 19.64 45.366 19.2888C45.0752 18.9674 45.5374 18.3818 45.8491 17.9869C45.9437 17.867 46.0245 17.7647 46.0661 17.6927C46.2253 17.4184 45.9042 17.1854 45.5351 16.9176C45.3049 16.7507 45.0562 16.5702 44.8935 16.3578C44.7921 16.2227 44.8262 15.9833 44.8635 15.7206C44.9267 15.2763 44.9993 14.7652 44.4419 14.5794C44.1526 14.4833 43.2558 15.022 43.575 16.5691C43.1678 18.0999 40.0672 20.7769 39.6537 19.6977C39.5531 19.4348 39.8188 18.9967 40.5297 18.848C41.7023 18.6041 41.6098 17.8786 41.0512 17.806C40.8742 17.7831 40.723 17.7647 40.5857 17.748L40.5857 17.748C40.0408 17.6819 39.7166 17.6425 38.8793 17.4469C38.4657 17.4034 38.4793 16.8538 38.9663 16.862H38.969ZM22.0037 18.5492C21.7217 18.8349 21.3962 19.2312 21.3526 19.5504C21.2583 20.2423 21.6129 20.8517 22.4925 20.8363C23.0776 20.8262 23.4192 20.5598 23.9701 20.13C24.3082 19.8663 24.7251 19.5411 25.3256 19.1759C25.4863 19.078 25.6613 18.9555 25.8297 18.8376C26.123 18.6322 26.3964 18.4408 26.5398 18.4177C26.6269 18.4177 26.753 18.444 26.7448 18.6109C26.7245 18.7067 26.7039 18.7978 26.6842 18.8852L26.6841 18.8855L26.6841 18.8856L26.6841 18.8857C26.5739 19.3728 26.4894 19.7465 26.6278 20.2015C26.7421 20.5797 27.2436 20.8164 27.6045 20.5434C27.9909 20.2507 28.1327 20.039 28.391 19.6534L28.391 19.6534C28.5353 19.4381 28.7159 19.1686 28.9956 18.8004C29.0183 18.7707 29.0516 18.7223 29.0923 18.6631L29.0923 18.6631C29.2837 18.3846 29.638 17.8691 29.819 17.9543C29.9942 18.0371 29.9541 18.2732 29.921 18.4686L29.917 18.4921C29.9054 18.5598 29.8926 18.6302 29.8794 18.7027L29.8794 18.7028C29.7056 19.6588 29.4695 20.9568 31.2355 20.7393C31.6872 20.6831 32.0508 20.3793 31.708 20.0555C31.5789 19.9338 31.4404 19.9427 31.2915 19.9523C31.2009 19.9582 31.1064 19.9643 31.0079 19.9413C30.7243 19.8759 30.8983 19.2408 31.0937 18.5279C31.3187 17.7065 31.572 16.7818 31.1866 16.5061C30.1897 15.7924 29.2641 17.0275 28.564 17.9617L28.5322 18.0042C28.2547 18.3742 28.0489 18.2119 27.8693 17.6904C27.6689 17.111 26.4655 17.5345 25.7745 17.8909C25.4399 18.0635 25.0191 18.3592 24.5901 18.6607C23.6064 19.3521 22.5792 20.074 22.4472 19.4135C22.4146 19.2493 22.6803 18.8839 23.1754 18.7043C23.2401 18.6808 23.3057 18.6576 23.3713 18.6343L23.3714 18.6343L23.3715 18.6343L23.3717 18.6342C23.895 18.4489 24.4133 18.2655 24.4133 17.9552C24.4133 17.685 24.2491 17.4955 24.0759 17.5045C23.8903 17.5149 23.6979 17.5341 23.4986 17.554C22.8271 17.6211 22.0769 17.696 21.2447 17.4674C20.9989 17.4655 20.8938 16.8471 21.622 16.9278C21.8151 16.9495 22.2441 16.9786 23.8764 17.0112C25.0254 17.0339 25.0508 16.1742 24.8966 15.7171C24.0346 15.9742 23.5539 15.9726 22.7248 15.9698C22.6354 15.9695 22.542 15.9692 22.4436 15.9692C21.3825 15.9692 19.9524 16.1161 20.0132 17.1382C20.0649 17.9988 21.2719 18.2346 21.8976 18.3053C22.0772 18.289 22.1334 18.4604 22.0037 18.5492ZM46.4504 16.4302C46.6299 16.4302 46.7841 16.3667 46.911 16.2388C47.038 16.111 47.1015 15.9568 47.1015 15.7772C47.1015 15.5977 47.038 15.4444 46.911 15.3184C46.7841 15.1923 46.629 15.1288 46.4504 15.1288C46.2717 15.1288 46.1184 15.1923 45.9924 15.3184C45.8654 15.4453 45.8019 15.5968 45.8019 15.7772C45.8019 15.9577 45.8645 16.1119 45.9906 16.2388C46.1166 16.3667 46.2708 16.4302 46.4504 16.4302ZM45.889 15.7782C45.889 15.6231 45.9434 15.4916 46.0532 15.3819C46.1638 15.2721 46.2953 15.2168 46.4513 15.2168C46.6072 15.2168 46.7405 15.2721 46.8503 15.3819C46.96 15.4916 47.0144 15.6222 47.0144 15.7782C47.0144 15.9341 46.96 16.0674 46.8503 16.1772C46.7405 16.2869 46.6081 16.3422 46.4513 16.3422C46.2944 16.3422 46.162 16.2869 46.0522 16.1772C45.9434 16.0674 45.889 15.9332 45.889 15.7782ZM46.6335 15.4435C46.5918 15.4263 46.5265 15.4181 46.4377 15.4181H46.1865V16.1318H46.308V15.8516H46.4095C46.4776 15.8516 46.5256 15.8589 46.5528 15.8743C46.5991 15.9006 46.6217 15.955 46.6217 16.0375V16.0937L46.6245 16.1164C46.6254 16.1191 46.6263 16.1219 46.6263 16.1246C46.6272 16.1273 46.6281 16.1318 46.6281 16.1318H46.7442L46.7405 16.1228C46.7378 16.1164 46.736 16.1046 46.7351 16.0856C46.7333 16.0674 46.7333 16.0493 46.7333 16.033V15.9813C46.7333 15.945 46.7206 15.9087 46.6943 15.8707C46.6689 15.8326 46.6281 15.8099 46.5719 15.8008C46.6163 15.7936 46.6508 15.7827 46.6753 15.7664C46.7215 15.7364 46.7451 15.6893 46.7451 15.6249C46.7451 15.5351 46.7079 15.4744 46.6335 15.4435ZM46.4186 15.5061C46.4903 15.5061 46.5419 15.5152 46.5746 15.5324L46.5737 15.5315C46.6063 15.5496 46.6227 15.5841 46.6227 15.6358C46.6227 15.6911 46.5973 15.7283 46.5474 15.7473C46.5202 15.7582 46.4794 15.7636 46.425 15.7636H46.3089V15.5061H46.4186Z" fill="#191A1F" />
                  <path d="M69.6455 21.622L70.4705 15.4261L73.0703 21.622H73.911L76.5107 15.4261L77.3358 21.622H79.7332L78.0675 11.2384H76.0749L73.4906 17.4499L70.9064 11.2384H68.9138L67.248 21.622H69.6455Z" fill="#191A1F" />
                  <path d="M88.1046 11.0049C86.0341 11.0049 84.742 11.83 84.0414 12.5305C83.1074 13.4646 82.438 14.9746 82.438 16.4691C82.438 17.9947 83.0607 19.3958 84.057 20.3299C84.8354 21.0771 86.1586 21.8555 88.1046 21.8555C89.8481 21.8555 91.1558 21.2639 92.121 20.3299C93.2419 19.2401 93.74 17.8235 93.74 16.438C93.74 14.8345 93.1018 13.4646 92.121 12.5149C91.078 11.503 89.7236 11.0049 88.1046 11.0049ZM88.089 13.2155C89.0386 13.2155 89.8637 13.6047 90.3774 14.1028C90.8445 14.5699 91.2804 15.3638 91.2804 16.438C91.2804 17.6367 90.7822 18.3683 90.3774 18.7575C89.8793 19.2557 89.132 19.6449 88.0734 19.6449C87.2639 19.6449 86.5167 19.4269 85.8473 18.7887C85.209 18.166 84.8977 17.4032 84.8977 16.4535C84.8977 15.3015 85.3647 14.5387 85.8161 14.1028C86.3766 13.5424 87.1861 13.2155 88.089 13.2155Z" fill="#191A1F" />
                  <path d="M96.9247 11.2384V21.622H100.287C101.159 21.622 102.949 21.513 104.382 20.0652C105.238 19.209 105.798 18.0259 105.798 16.3913C105.798 14.7722 105.222 13.558 104.304 12.6706C103.027 11.4408 101.579 11.2384 100.209 11.2384H96.9247ZM99.3221 13.2622H99.9604C100.692 13.2622 101.704 13.3712 102.482 14.1495C102.887 14.5543 103.339 15.2704 103.339 16.3913C103.339 17.5433 102.887 18.4306 102.327 18.8976C101.548 19.5515 100.459 19.5982 99.9604 19.5982H99.3221V13.2622Z" fill="#191A1F" />
                  <path d="M120.447 12.0323C119.233 11.1294 118.081 11.0049 117.364 11.0049C116.399 11.0049 115.527 11.2073 114.796 11.9545C114.173 12.5928 113.893 13.3712 113.893 14.2896C113.893 14.7878 113.971 15.5195 114.516 16.0955C114.92 16.5314 115.481 16.7493 115.963 16.9206L116.82 17.2163C117.115 17.3253 117.754 17.5588 118.034 17.7923C118.252 17.9792 118.392 18.1971 118.392 18.5396C118.392 18.9288 118.221 19.209 118.018 19.3802C117.676 19.676 117.24 19.7383 116.929 19.7383C116.446 19.7383 116.026 19.6137 115.621 19.3647C115.341 19.1934 114.92 18.8354 114.656 18.5707L113.41 20.2832C113.799 20.6723 114.407 21.1549 114.936 21.4196C115.59 21.7465 116.244 21.8555 116.991 21.8555C117.676 21.8555 119.015 21.7621 119.933 20.7969C120.478 20.2365 120.852 19.3024 120.852 18.2282C120.852 17.6211 120.696 16.8739 120.089 16.2979C119.684 15.9087 119.124 15.6752 118.672 15.5039L117.894 15.2081C117.193 14.9435 116.835 14.8501 116.586 14.6166C116.43 14.4765 116.353 14.2896 116.353 14.0406C116.353 13.7759 116.461 13.5424 116.617 13.3867C116.897 13.0754 117.302 13.0287 117.629 13.0287C117.925 13.0287 118.594 13.0754 119.373 13.7759L120.447 12.0323Z" fill="#191A1F" />
                  <path d="M126.375 11.2384H123.978V21.622H126.375V17.2163H130.516V21.622H132.913V11.2384H130.516V15.1926H126.375V11.2384Z" fill="#191A1F" />
                  <path d="M141.767 11.0049C139.697 11.0049 138.404 11.83 137.704 12.5305C136.77 13.4646 136.1 14.9746 136.1 16.4691C136.1 17.9947 136.723 19.3958 137.719 20.3299C138.498 21.0771 139.821 21.8555 141.767 21.8555C143.511 21.8555 144.818 21.2639 145.784 20.3299C146.904 19.2401 147.403 17.8235 147.403 16.438C147.403 14.8345 146.764 13.4646 145.784 12.5149C144.74 11.503 143.386 11.0049 141.767 11.0049ZM141.751 13.2155C142.701 13.2155 143.526 13.6047 144.04 14.1028C144.507 14.5699 144.943 15.3638 144.943 16.438C144.943 17.6367 144.445 18.3683 144.04 18.7575C143.542 19.2557 142.795 19.6449 141.736 19.6449C140.926 19.6449 140.179 19.4269 139.51 18.7887C138.871 18.166 138.56 17.4032 138.56 16.4535C138.56 15.3015 139.027 14.5387 139.479 14.1028C140.039 13.5424 140.849 13.2155 141.751 13.2155Z" fill="#191A1F" />
                  <path d="M150.587 11.2384V21.622H152.985V17.9792H154.448C155.18 17.9792 156.269 17.9013 157.094 17.0607C157.484 16.6559 157.982 15.9242 157.982 14.6166C157.982 13.9005 157.857 12.873 156.97 12.0946C156.145 11.3629 155.148 11.2384 154.246 11.2384H150.587ZM152.985 13.1999H153.794C154.261 13.1999 154.775 13.2466 155.133 13.5891C155.366 13.807 155.522 14.1651 155.522 14.601C155.522 15.0525 155.351 15.4105 155.133 15.6285C154.744 16.0021 154.168 16.0176 153.841 16.0176H152.985V13.1999Z" fill="#191A1F" />
                  <defs>
                    <linearGradient id="paint0_linear_15137_9765" x1="-8.07515" y1="16" x2="172.719" y2="16" gradientUnits="userSpaceOnUse">
                      <stop stop-color="#E8C722" />
                      <stop offset="0.193478" stop-color="#8DD7BC" />
                      <stop offset="0.397826" stop-color="#00C5BB" />
                      <stop offset="0.619565" stop-color="#0197CF" />
                      <stop offset="0.780435" stop-color="#FC456E" />
                      <stop offset="1" stop-color="#E46053" />
                    </linearGradient>
                  </defs>
                </svg>
                <CloseButton onClick={() => setIsOpen(false)}>
                  <SvgX />
                </CloseButton>
              </Header>

              <Divider />

              <Title>{translatedTitle}!</Title>

              <ProductContainer>
                <ProductImageContainer><ProductImage src={productImage} />
                </ProductImageContainer>
                <ProductDetails>
                  <ProductTitle>Mod Shop Stratocaster</ProductTitle>
                  <ItemList>
                    {specNames.slice(0, showMore ? specNames.length : 4).map((name, index) => (
                      <Item key={index}>•&nbsp;&nbsp;{name}</Item>
                    ))}
                  </ItemList>
                  {specNames.length > 4 && (
                    <ShowMoreButton onClick={() => setShowMore(prev => !prev)}>
                      {showMore ? translatedShowLess : translatedShowMore}
                    </ShowMoreButton>
                  )}
                </ProductDetails>
                <ProductPrice>{currencySymbol + productPrice}</ProductPrice>
              </ProductContainer>

              <Divider />
              {locale.indexOf("US") !== -1 && (
                <AddonComponent profile={searchProfile} subtitle="Complete Your Setup!" />
              )}              <Divider />

              <ButtonContainer>
                <ButtonOne onClick={() => (window.location.href = '/')}>{translatedKeepShopping}</ButtonOne>
                <ButtonTwo onClick={() => (window.location.href = '/cart')}>{translatedContinueToCart}</ButtonTwo>
              </ButtonContainer>
            </DrawerContainer>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default ModShopDrawer;
